import React, { useState } from 'react';
import { apiGet } from '../../lib/api';
import { ProjectStrictPicker } from '../../components/pickers/ProjectClientPickers';
import { Calculator, ArrowUpCircle, ArrowDownCircle, AlertTriangle, Activity } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { formatINR } from '../../lib/format';

const Row = ({ label, value, bold, tone, testId }) => (
  <div className={`flex items-center justify-between py-1.5 ${bold ? 'border-t border-slate-200 mt-1 pt-2' : ''}`} data-testid={testId}>
    <span className={`text-xs ${bold ? 'font-display font-bold' : 'text-slate-600'}`}>{label}</span>
    <span className={`text-xs ${bold ? 'font-display font-extrabold' : ''} ${tone === 'red' ? 'text-red-600' : tone === 'green' ? 'text-emerald-700' : ''}`}>{formatINR(value)}</span>
  </div>
);

export default function ProjectPnL() {
  const [project, setProject] = useState('');
  const [d, setD] = useState(null);
  const [poc, setPoc] = useState(null);
  const [loading, setLoading] = useState(false);

  const onPick = (p) => {
    setProject(p?.name || '');
    setD(null);
    setPoc(null);
    if (p?.id) {
      setLoading(true);
      Promise.all([
        apiGet(`/finance/project-pnl/${p.id}`).catch(() => null),
        apiGet(`/finance/poc/${p.id}`).catch(() => null),
      ]).then(([pnl, pocData]) => {
        setD(pnl);
        setPoc(pocData);
      }).finally(() => setLoading(false));
    }
  };

  return (
    <div className="space-y-5 p-6 md:p-8 max-w-4xl" data-testid="project-pnl-page">
      <div>
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">Finance & Accounts</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Project P&amp;L</h1>
        <p className="text-sm text-slate-600 mt-1">Revenue vs cost with EVM overlay, per project.</p>
      </div>

      <div className="max-w-md" data-testid="pnl-project-picker">
        <ProjectStrictPicker value={project} onChange={setProject} onPick={onPick} testid="pnl-project" />
      </div>

      {loading && <div className="text-slate-500 text-sm">Loading…</div>}
      {!loading && !d && <div className="text-center py-16 text-slate-400"><Calculator size={40} className="mx-auto mb-3 opacity-40" /><p className="text-sm">Select a project to view its P&amp;L.</p></div>}

      {d && (
        <>
          <div className="grid md:grid-cols-2 gap-5">
            <Card data-testid="pnl-revenue-card">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-display uppercase tracking-[0.16em] text-emerald-700 flex items-center gap-2"><ArrowUpCircle size={15} /> Revenue</CardTitle></CardHeader>
              <CardContent>
                <Row label="RA Bills Raised" value={d.revenue.ra_bills_raised} testId="rev-raised" />
                <Row label="RA Bills Collected" value={d.revenue.ra_bills_collected} testId="rev-collected" />
                <Row label="Retention Held" value={d.revenue.retention_held} />
                <Row label="TDS Deducted by Client" value={d.revenue.tds_deducted_by_client} />
              </CardContent>
            </Card>
            <Card data-testid="pnl-cost-card">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-display uppercase tracking-[0.16em] text-red-600 flex items-center gap-2"><ArrowDownCircle size={15} /> Cost</CardTitle></CardHeader>
              <CardContent>
                <Row label="Procurement (materials)" value={d.cost.procurement} />
                <Row label="Labour (payroll)" value={d.cost.labour} />
                <Row label="Subcontractor" value={d.cost.subcontractor} />
                <Row label="Petty Cash" value={d.cost.petty_cash} />
                <Row label="Fuel" value={d.cost.fuel} />
                <Row label="Total Cost" value={d.cost.total} bold testId="cost-total" />
              </CardContent>
            </Card>
          </div>

          <Card data-testid="pnl-margin-card">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-display uppercase tracking-[0.16em] text-slate-500">Margin</CardTitle></CardHeader>
            <CardContent>
              <Row label="Revenue basis" value={d.margin.revenue_basis} />
              <Row label="Total cost" value={d.cost.total} />
              <Row label={`Gross Margin${d.margin.gross_margin_pct != null ? ` (${d.margin.gross_margin_pct}%)` : ''}`} value={d.margin.gross_margin} bold tone={d.margin.gross_margin >= 0 ? 'green' : 'red'} testId="gross-margin" />
            </CardContent>
          </Card>

          {d.evm && (
            <Card data-testid="pnl-evm-card">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-display uppercase tracking-[0.16em] text-slate-500">Earned Value (EVM)</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-center">
                  {[['BAC', d.evm.BAC], ['EV', d.evm.EV], ['AC', d.evm.AC], ['EAC', d.evm.EAC], ['VAC', d.evm.VAC]].map(([k, v]) => (
                    <div key={k} className={`rounded-sm border p-3 ${k === 'VAC' && v != null && v < 0 ? 'border-red-200 bg-red-50' : 'border-slate-200'}`} data-testid={`evm-${k}`}>
                      <div className="text-[10px] text-slate-500 font-bold">{k}</div>
                      <div className="text-xs font-display font-bold mt-1">{v != null ? formatINR(v) : '—'}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {poc && (
            <>
              {poc.is_onerous && (
                <div className="border-2 border-rose-300 bg-rose-50 rounded-sm p-4 flex items-start gap-3" data-testid="pnl-onerous-banner">
                  <AlertTriangle size={20} className="text-rose-700 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-display font-bold text-rose-800">Onerous Contract (AS-7)</div>
                    <p className="text-sm text-rose-700 mt-0.5">
                      Total estimated cost exceeds contract value. Recognise the full expected loss
                      of <b>{formatINR(poc.expected_loss)}</b> immediately — not pro-rata over remaining work.
                    </p>
                  </div>
                </div>
              )}

              <Card data-testid="pnl-revenue-recognition-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-display uppercase tracking-[0.16em] text-[#3b4872] flex items-center gap-2">
                    <Activity size={14} /> Revenue Recognition (POC — cost-to-cost)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    <div className="rounded-sm border border-slate-200 p-3" data-testid="poc-pct">
                      <div className="text-[10px] text-slate-500 font-bold uppercase">POC %</div>
                      <div className="text-lg font-display font-extrabold mt-1">{poc.poc_pct}%</div>
                    </div>
                    <div className="rounded-sm border border-slate-200 p-3">
                      <div className="text-[10px] text-slate-500 font-bold uppercase">Revenue Recognised</div>
                      <div className="text-base font-display font-extrabold mt-1">{formatINR(poc.revenue_recognised)}</div>
                    </div>
                    <div className="rounded-sm border border-slate-200 p-3">
                      <div className="text-[10px] text-slate-500 font-bold uppercase">Billed</div>
                      <div className="text-base font-display font-extrabold mt-1">{formatINR(poc.billed)}</div>
                    </div>
                    <div className={`rounded-sm border p-3 ${poc.over_under_billing < 0 ? 'border-rose-200 bg-rose-50' : 'border-emerald-200 bg-emerald-50'}`} data-testid="poc-over-under">
                      <div className="text-[10px] text-slate-500 font-bold uppercase">{poc.over_under_billing >= 0 ? 'Under-billed (Contract Asset)' : 'Over-billed (Contract Liability)'}</div>
                      <div className={`text-base font-display font-extrabold mt-1 ${poc.over_under_billing < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                        {formatINR(Math.abs(poc.over_under_billing))}
                      </div>
                    </div>
                  </div>
                  <div className="text-[11px] text-slate-500">
                    Contract value: <b>{formatINR(poc.contract_value)}</b> · Estimated total cost:
                    <b> {formatINR(poc.estimated_cost_at_completion ?? poc.bac)}</b>
                    {poc.estimated_cost_at_completion != null && <span className="text-[#3b4872]"> (manually overridden)</span>}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}
    </div>
  );
}
